import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserSessionStateService } from '../services/user-session-state.service';

/**
 * Surfaces the terminal "you've exhausted the anonymous AI budget" state.
 *
 * Trips when the BE returns `429 + error.extensions.code === "RATE_LIMIT_EXCEEDED"`
 * (contract emitted by dr-plg-service's rate-limit guard — see the
 * `JSONResponse(status_code=429, content={"message": ..., "extensions":
 * {"code": "RATE_LIMIT_EXCEEDED"}})` shape in the service). Latches
 * `UserSessionStateService.rateLimitBlocked$` so `OnboardingSetupComponent`
 * renders `RateLimitOverlayComponent` over the wizard card, then re-throws so
 * caller `finalize()` blocks still tear down their loading flags.
 *
 * The status-only fallback (429 without the code) is intentionally NOT
 * triggered: a bare 429 from an upstream proxy (Azure Front Door, ingress)
 * is not the same product state and should fall through to whatever generic
 * error handling the caller already has. Keep this interceptor narrow.
 */
@Injectable()
export class RateLimitInterceptor implements HttpInterceptor {
    private readonly state = inject(UserSessionStateService);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(req).pipe(
            catchError((err: unknown) => {
                if (isRateLimited(err)) {
                    const message = (err.error as { message?: unknown } | null)?.message;
                    this.state.notifyRateLimited(typeof message === 'string' ? message : null);
                }
                return throwError(() => err);
            }),
        );
    }
}

function isRateLimited(err: unknown): err is HttpErrorResponse {
    if (!(err instanceof HttpErrorResponse) || err.status !== 429) {
        return false;
    }
    const code = (err.error as { extensions?: { code?: unknown } } | null)?.extensions?.code;
    return code === 'RATE_LIMIT_EXCEEDED';
}
